import React, { FC } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Link,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

interface HipaaNoticeDialogProps {
  openDialog: boolean;
  onClose: () => void;
}

const useStyles = makeStyles((theme) => ({
  list: {
    paddingLeft: theme.spacing(2),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    "& li": {
      marginBottom: theme.spacing(1),
    },
  },
}));

const HipaaNoticeDialog: FC<HipaaNoticeDialogProps> = ({
  openDialog,
  onClose,
}) => {
  const classes = useStyles();
  return (
    <Dialog open={openDialog} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography>
        <Typography variant="subtitle1">
          HIPAA NOTICE OF PRIVACY PRACTICES
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle1" gutterBottom>
          Revised Effective date: December 31, 2024
        </Typography>
        <Typography variant="subtitle1" paragraph>
          <b>
            THIS NOTICE DESCRIBES HOW MEDICAL INFORMATION ABOUT YOU MAY BE USED
            AND DISCLOSED AND HOW YOU CAN GET ACCESS TO THIS INFORMATION. PLEASE
            REVIEW IT CAREFULLY.
          </b>
        </Typography>
        <Typography variant="subtitle1" paragraph>
          Baylor Genetics has specific duties regarding your medical
          information. We understand that medical information about you and your
          health is personal. We are committed to protecting medical information
          about you.
        </Typography>
        <Typography variant="subtitle1" paragraph>
          This notice will tell you about the ways in which we may use and
          disclose medical information about you. We also describe your rights
          and certain obligations we have regarding the use and disclosure of
          your medical information.
        </Typography>
        <Typography variant="subtitle1">
          <b>YOUR RIGHTS</b>
        </Typography>
        <Typography variant="subtitle1" paragraph>
          When it comes to your medical information, you have certain rights.
          This section explains your rights and some of our responsibilities to
          help you. You may ask us how to exercise your rights.
        </Typography>
        <Typography variant="subtitle1" component="div" paragraph>
          <b>Get an electronic or paper copy of your medical record.</b>
          <br />
          <ul className={classes.list}>
            <li>
              You can ask to see or get an electronic or paper copy of the
              medical information we have about you.
            </li>
            <li>
              We will provide a copy or a summary of your medical information,
              usually within 15 days of your request if it is in electronic
              format. We may charge a reasonable, cost-based fee.
            </li>
          </ul>
        </Typography>
        <Typography variant="subtitle1" component="div" paragraph>
          <b>Ask us to correct your medical record.</b>
          <br />
          <ul className={classes.list}>
            <li>
              You can ask us to correct medical information about you that you
              think is incorrect or incomplete.
            </li>
            <li>
              We may say “no” to your request, but we will tell you why in
              writing within 60 days.
            </li>
          </ul>
        </Typography>
        <Typography variant="subtitle1" component="div" paragraph>
          <b>Request confidential communications.</b>
          <br />
          <ul className={classes.list}>
            <li>
              You can ask us to contact you in a specific way (for example, at
              your home or office phone) or to send mail to a different address
              than what we have on file.
            </li>
            <li>We will say “yes” to all reasonable requests.</li>
          </ul>
        </Typography>
        <Typography variant="subtitle1" component="div" paragraph>
          <b>Ask us to limit what we use or share.</b>
          <br />
          <ul className={classes.list}>
            <li>
              You can ask us not to use or share certain medical information for
              treatment, payment, or our operations. We are not required to
              agree to your request, and we may say “no” if it would affect your
              care.
            </li>
            <li>
              If you pay for a service out-of-pocket in full, you can ask us not
              to share that information for the purpose of payment or our
              operations with your health insurer. We will say “yes” unless a
              law requires us to share that information.
            </li>
          </ul>
        </Typography>
        <Typography variant="subtitle1" component="div" paragraph>
          <b>
            Get a list of those with whom we have shared your medical
            information.
          </b>
          <br />
          <ul className={classes.list}>
            <li>
              You can ask for a list (accounting) of the times we have shared
              your medical information for six (6) years prior to the date you
              ask, to include information on who we shared it with, and why.
            </li>
            <li>
              We will include all the disclosures except for those about
              treatment, payment, and our operations, as well as certain other
              disclosures (such as any you asked us to make). We will provide
              one accounting a year for free, but may charge a reasonable,
              cost-based fee if you ask for another one within 12 months from
              your previous request.
            </li>
          </ul>
        </Typography>
        <Typography variant="subtitle1" component="div" paragraph>
          <b>Get a copy of this privacy notice.</b>
          <br />
          <ul className={classes.list}>
            <li>
              You can ask for a paper copy of this notice at any time, even if
              you have agreed to receive the notice electronically. We will
              provide you with a paper copy promptly.
            </li>
          </ul>
        </Typography>
        <Typography variant="subtitle1" component="div" paragraph>
          <b>Choose someone to act for you.</b>
          <br />
          <ul className={classes.list}>
            <li>
              If you have given someone medical power of attorney or if someone
              is your legal guardian, that person can exercise your rights and
              make choices about your medical information.
            </li>
            <li>
              We will make sure the person has authority and can act for you
              before we take any action.
            </li>
          </ul>
        </Typography>
        <Typography variant="subtitle1" component="div" paragraph>
          <b>File a complaint if you feel your rights are violated.</b>
          <br />
          <ul className={classes.list}>
            <li>
              You can complain if you feel we have violated your privacy rights
              by contacting the Baylor Genetics Compliance Officer, 2450
              Holcombe Blvd, Ste.2210, Houston, TX 77021, 1-281-849-6995 or at
              compliance@baylorgenetics.com
            </li>
            <li>
              You can file a complaint with the U.S. Department of Health and
              Human Services Office for Civil Rights by sending a letter to 200
              Independence Avenue, S.W., Washington, D.C. 20201, calling
              1-877-696-6775, or visiting{" "}
              <a
                href="https://www.hhs.gov/ocr/privacy/hipaa/complaints/"
                target="_blank"
                rel="noopener noreferrer"
              >
                www.hhs.gov/ocr/privacy/hipaa/complaints/
              </a>
              .
            </li>
            <li>We will not retaliate against you for filing a complaint.</li>
          </ul>
        </Typography>
        <Typography variant="subtitle1">
          <b>YOUR CHOICES</b>
        </Typography>
        <Typography variant="subtitle1" paragraph>
          <span>
            <b>
              For certain medical information, you can tell us your choices
              about what we share.
            </b>
          </span>{" "}
          If you have a clear preference for how we share your medical
          information in the situations described below, talk to us. Tell us
          what you want us to do, and we will follow your instructions.
        </Typography>
        <Typography variant="subtitle1" paragraph>
          In these cases, you have both the right and choice to tell us to:
        </Typography>
        <Typography variant="subtitle1" component="div" paragraph>
          <ul className={classes.list}>
            <li>
              Share information with your family, close friends, or others
              involved in your care
            </li>
            <li>Share information in a disaster relief situation</li>
          </ul>
        </Typography>
        <Typography variant="subtitle1" paragraph>
          <i>
            If you are not able to tell us your preference, for example if you
            are unconscious, we may go ahead and share your information if we
            believe it is in your best interest. We may also share your
            information when needed to lessen a serious and imminent threat to
            health or safety.
          </i>
        </Typography>
        <Typography variant="subtitle1" paragraph>
          In these cases, we never share your information unless you give us
          written permission:
        </Typography>
        <Typography variant="subtitle1" component="div" paragraph>
          <ul className={classes.list}>
            <li>Marketing purposes</li>
            <li>Sale of your information</li>
          </ul>
        </Typography>
        <Typography variant="subtitle1" paragraph>
          You may revoke your written permission at any time by providing us
          with a written request. We will no longer use or disclosure your
          medical information after we receive your written request to revoke
          your written permission.
        </Typography>
        <Typography variant="subtitle1" paragraph>
          We do not conduct fundraising and will not contact you for fundraising
          efforts.
        </Typography>
        <Typography variant="subtitle1">
          <b>HOW WE MAY USE AND DISCLOSE YOUR MEDICAL INFORMATION</b>
        </Typography>
        <Typography variant="subtitle1" paragraph>
          <b>For Treatment:</b> We can use your medical information and share it
          with other professionals who are treating you. Example: A doctor
          treating you may ask us for genetic testing information we have about
          you.
        </Typography>
        <Typography variant="subtitle1" paragraph>
          <b>For Payment:</b> We can use and share your medical information to
          bill and get payment from health plans or other entities. Example: We
          give information about you to your health insurance plan so it will
          pay for your services.
        </Typography>
        <Typography variant="subtitle1" paragraph>
          <b>For Health Care Operations:</b> We can use your medical information
          to run our operations, improve your care, and contact you when
          necessary. Example: We use medical information about you to assess the
          quality of our services and to evaluate the performance of our staff.
        </Typography>
        <Typography variant="subtitle1">
          <b>HOW ELSE CAN WE USE AND DISCLOSE YOUR MEDICAL INFORMATION</b>
        </Typography>
        <Typography variant="subtitle1" paragraph>
          We are allowed or required to share your medical information in other
          ways without your written authorization. This is usually in ways that
          contribute to the public good, such as public health and research. We
          have to meet many conditions in the law before we can share your
          medical information for these purposes. For more information see:{" "}
          <Link
            href="https://www.hhs.gov/ocr/privacy/hipaa/understanding/consumers/index.html"
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
            underline="always"
          >
            www.hhs.gov/ocr/privacy/hipaa/understanding/consumers/index.html
          </Link>
          .
        </Typography>
        <Typography variant="subtitle1" paragraph>
          <b>Help with public health and safety issues.</b> We can share medical
          information about you for certain situations, such as:
        </Typography>
        <Typography variant="subtitle1" component="div" paragraph>
          <ul className={classes.list}>
            <li>Preventing Disease</li>
            <li>Reporting suspected abuse, neglect, or domestic violence</li>
            <li>Helping with product recalls</li>
            <li>Reporting adverse reactions to medications</li>
            <li>
              Preventing or reducing a serious threat to anyone’s health or
              safety
            </li>
          </ul>
        </Typography>
        <Typography variant="subtitle1" paragraph>
          <b>Do research.</b> We can use or share your medical information to
          perform research
        </Typography>
        <Typography variant="subtitle1" paragraph>
          <b>Compliance with the law.</b> We will share information about you if
          state or federal laws require it, including with the Department of
          Health and Human Services if it wants to see that we are complying
          with federal privacy law.
        </Typography>
        <Typography variant="subtitle1" paragraph>
          <b>Respond to organ and tissue donation requests.</b> We can share
          medical information about you with organ procurement organizations.
        </Typography>
        <Typography variant="subtitle1" paragraph>
          <b>Work with a medical examiner or funeral director.</b> We can share
          medical information with a coroner, medical examiner, or funeral
          director when an individual dies.
        </Typography>
        <Typography variant="subtitle1" paragraph>
          <b>
            Address worker’s compensation, law enforcement, and other government
            requests.
          </b>{" "}
          We can use or share medical information about you:
        </Typography>
        <Typography variant="subtitle1" component="div" paragraph>
          <ul className={classes.list}>
            <li>For worker’s compensation claims</li>
            <li>
              For law enforcement purposes or with a law enforcement official
            </li>
            <li>
              With health oversight agencies for activities authorized by law
            </li>
            <li>
              For special government functions such as military, national
              security, and presidential protective services.
            </li>
          </ul>
        </Typography>
        <Typography variant="subtitle1" paragraph>
          <b>Respond to lawsuits and legal actions.</b> We can share medical
          information about you in response to a court or administrative order,
          or in response to subpoena.
        </Typography>
        <Typography variant="subtitle1">
          <b>
            USES AND DISCLOSURES OF YOUR MEDICAL INFORMATION: REPRODUCTIVE
            HEALTH CARE
          </b>
        </Typography>
        <Typography variant="subtitle1" paragraph>
          We are prohibited from sharing your medical information related to
          reproductive health care for the following activities:
        </Typography>
        <Typography variant="subtitle1" component="div" paragraph>
          <ul className={classes.list}>
            <li>
              To conduct a criminal, civil or administrative investigation or
              impose penalties on any person for seeking, obtaining, providing
              or facilitating lawful reproductive health care.
              <br />
              <span>
                <i>
                  Example: We will not share your medical information if we know
                  it will be used to investigate, prosecute or impose a penalty
                  on your provider who orders testing used in making
                  reproductive health care decisions.
                </i>
              </span>
            </li>
            <li>
              To identify any person seeking, obtaining, providing or
              facilitating reproductive health care.
              <br />
              <span>
                <i>
                  Example: We will not share your medical information with
                  anyone who wants to identify you or your health care provider
                  regarding decisions made about your reproductive health care
                  services.
                </i>
              </span>
            </li>
          </ul>
        </Typography>
        <Typography variant="subtitle1" paragraph>
          We will obtain a written attestation that your medical information
          related to reproductive care will not be used for prohibited purposes
          when it is requested from:
        </Typography>
        <Typography variant="subtitle1" component="div" paragraph>
          <ul className={classes.list}>
            <li>Health Oversight agencies</li>
            <li>Judicial or administrative courts</li>
            <li>Law Enforcement, including requests to identify you.</li>
            <li>Coroner or Medical Examiner</li>
          </ul>
        </Typography>
        <Typography variant="subtitle1" paragraph>
          For example, we will obtain a written attestation before disclosing
          your medical information related to reproductive health care when
          requested under a court order.
        </Typography>
        <Typography variant="subtitle1" paragraph>
          Your medical information disclosed to a third party may no longer be
          protected by HIPAA and may be redisclosed by the third party.
        </Typography>
        <Typography variant="subtitle1">
          <b>OUR RESPONSIBILITIES</b>
        </Typography>
        <Typography variant="subtitle1" paragraph>
          <ul>
            <li>
              We are required by law to maintain the privacy and security of
              your protected medical information.
            </li>
            <li>
              We will let you know promptly if a breach occurs that may have
              compromised the privacy or security of your information.
            </li>
            <li>
              We must follow the duties and privacy practices described in this
              notice and give you a copy of it.
            </li>
            <li>
              We will not use or share your information other than as described
              in this notice unless you tell us we can in writing. If you tell
              us we can, you may change your mind at any time. Let us know in
              writing if you change your mind.
            </li>
          </ul>
          For more information see:{" "}
          <Link
            href="https://www.hhs.gov/hipaa/for-individuals/notice-privacy-practices/index.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            www.hhs.gov/hipaa/for-individuals/notice-privacy-practices/index.html
          </Link>
        </Typography>
        <Typography variant="subtitle1">
          <b>WE RESERVE THE RIGHT TO CHANGE THIS NOTICE</b>
        </Typography>
        <Typography variant="subtitle1" paragraph>
          We can change the terms of this notice, and the changes will apply to
          all information we have about you. The new notice will be available
          upon request and on our website at{" "}
          <Link
            href="https://www.baylorgenetics.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            www.baylorgenetics.com
          </Link>
          .
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HipaaNoticeDialog;
